import React, { useState, useEffect } from 'react';
import { Form, Select, Input, Message, Icon, Popup, Dropdown } from 'semantic-ui-react';
import styled from 'styled-components';
import { StepComponentProps } from './OrganizationWizard';
import { surveyQuestionApi } from '../../survey-questions/api/surveyQuestionApi';
import { QuestionStatus } from '../../survey-questions/constants/SurveyQuestionTypes';
import { validateCronExpression } from './OrganizationReminders';
import LoadingSpinner from 'common/components/LoadingSpinner';
import { organizationApi } from '../api/organizationApi';
import { useToasts } from 'common/toast';

const SurveyType = {
  EDI: 7,
  Outcomes: 10,
};

const QUICK_SCHEDULES = [
  { label: 'Mon, Wed, Fri at 9 AM', value: '0 9 * * 1,3,5' },
  { label: 'Every weekday at 9 AM', value: '0 9 * * 1-5' },
  { label: 'Monday at 9 AM', value: '0 9 * * 1' },
  { label: 'Every day at 9 AM', value: '0 9 * * *' },
];

const OrganizationWizardStep4Survey: React.FC<StepComponentProps> = ({
  data,
  onDataChange,
  onNext,
  onBack,
  onCancel,
}) => {
  const { addToast } = useToasts();
  const [surveySettings, setSurveySettings] = useState(data.surveySettings);
  const [cronErrors, setCronErrors] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);

  const { data: org } = organizationApi.useApiAdminV1OrganizationRetrieveQuery(
    { id: data.organizationId ?? -1 },
    { skip: !data.organizationId }
  );

  const [updateOrganization] = organizationApi.useApiAdminV1OrganizationPartialUpdateMutation();

  const { data: questions = [], isLoading } =
    surveyQuestionApi.useApiAdminV1SurveyQuestionListQuery({});

  const pillarQuestions = questions
    .filter(q => q.survey_type === SurveyType.EDI && q.status === QuestionStatus.ACTIVE)
    .map(q => ({ key: q.id, value: q.id, text: q.name }));

  const outcomeQuestions = questions
    .filter(q => q.survey_type === SurveyType.Outcomes && q.status === QuestionStatus.ACTIVE)
    .map(q => ({ key: q.id, value: q.id, text: q.name }));

  // Set first EDI question as default if not already set
  useEffect(() => {
    if (pillarQuestions.length > 0 && surveySettings.default_survey_question === null) {
      setSurveySettings(prev => ({ ...prev, default_survey_question: pillarQuestions[0].value as number }));
    }
  }, [pillarQuestions.length]);

  const handleFieldChange = (field: string, value: any) => {
    const updated = { ...surveySettings, [field]: value };
    setSurveySettings(updated);
    // persist to wizard data so navigating back retains edits
    onDataChange({ surveySettings: updated });

    if (field === 'default_reminder_schedule') {
      // Validate cron on change
      const validation = validateCronExpression(value);
      setCronErrors(validation.valid ? '' : validation.error || 'Invalid cron expression');
    }
  };

  const handleContinue = async () => {
    // Validate cron expression before continuing
    if (surveySettings.default_reminder_schedule) {
      const validation = validateCronExpression(surveySettings.default_reminder_schedule);
      if (!validation.valid) {
        setCronErrors(validation.error || 'Invalid cron expression');
        return;
      }
    }

    onDataChange({ surveySettings });

    // Persist to organization
    if (data.organizationId) {
      setIsSaving(true);
      try {
        await updateOrganization({
          id: data.organizationId,
          patchedOrganization: {
            default_survey_question: surveySettings.default_survey_question,
            default_outcome_question: surveySettings.default_outcome_question,
            default_min_followup: surveySettings.default_min_followup,
            default_max_followup: surveySettings.default_max_followup,
            default_reminder_schedule: surveySettings.default_reminder_schedule,
          },
        } as any).unwrap();
        addToast('Survey settings saved', { appearance: 'success' });
      } catch (err) {
        console.error('Failed to save survey settings:', err);
        addToast('Failed to save survey settings', { appearance: 'error' });
      } finally {
        setIsSaving(false);
      }
    }

    onNext();
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <StepContainer>
      <StepHeader>
        <StepTitle>Survey Configuration</StepTitle>
        <StepSubtitle>
          Configure survey questions and reminder settings. These are optional and can be configured later.
        </StepSubtitle>
      </StepHeader>

      <SectionCard>
        <Form>
          <Form.Group widths="equal">
            <Form.Field required>
              <label>Survey Questions</label>
              <Select
                placeholder="Select survey question set"
                options={pillarQuestions}
                value={surveySettings.default_survey_question ?? undefined}
                onChange={(_, { value }) => handleFieldChange('default_survey_question', value)}
              />
            </Form.Field>

            <Form.Field>
              <label>Outcome Questions</label>
              <Select
                placeholder="Select outcome question set (optional)"
                options={outcomeQuestions}
                value={surveySettings.default_outcome_question ?? undefined}
                onChange={(_, { value }) => handleFieldChange('default_outcome_question', value)}
                clearable
              />
            </Form.Field>
          </Form.Group>

          <Form.Group widths="equal">
            <Form.Field>
              <label>Min Follow-up Questions</label>
              <Input
                type="number"
                placeholder="e.g., 2"
                value={surveySettings.default_min_followup ?? 2}
                onChange={(_, { value }) => handleFieldChange('default_min_followup', value ? Number(value) : null)}
              />
            </Form.Field>

            <Form.Field>
              <label>Max Follow-up Questions</label>
              <Input
                type="number"
                placeholder="e.g., 5"
                value={surveySettings.default_max_followup ?? 2}
                onChange={(_, { value }) => handleFieldChange('default_max_followup', value ? Number(value) : null)}
              />
            </Form.Field>
          </Form.Group>
          <Form.Field error={!!cronErrors}>
            <label>Cron Schedule</label>
            <CronInputWrapper>
              <Input
                placeholder="0 9 * * 1,3,5"
                value={surveySettings.default_reminder_schedule}
                onChange={(_, { value }) => handleFieldChange('default_reminder_schedule', value)}
                style={{ flex: 1 }}
              />
              <Dropdown
                icon={null}
                trigger={<IconButton type="button"><Icon name="lightning" /></IconButton>}
                pointing="top right"
              >
                <Dropdown.Menu>
                  {QUICK_SCHEDULES.map((schedule) => (
                    <Dropdown.Item
                      key={schedule.value}
                      text={schedule.label}
                      onClick={() => handleFieldChange('default_reminder_schedule', schedule.value)}
                    />
                  ))}
                </Dropdown.Menu>
              </Dropdown>
              <Popup
                trigger={<IconButton type="button"><Icon name="question circle" /></IconButton>}
                on="click"
                position="bottom right"
                wide="very"
              >
                <Popup.Header>Common Cron Examples</Popup.Header>
                <Popup.Content>
                  <ExampleTable>
                    <tbody>
                      <tr>
                        <td><code>0 9 * * 1,3,5</code></td>
                        <td>Every Monday, Wednesday, Friday at 9:00 AM</td>
                      </tr>
                      <tr>
                        <td><code>0 9 * * 1-5</code></td>
                        <td>Every weekday at 9:00 AM</td>
                      </tr>
                      <tr>
                        <td><code>0 9 * * 1</code></td>
                        <td>Every Monday at 9:00 AM</td>
                      </tr>
                      <tr>
                        <td><code>0 9 * * *</code></td>
                        <td>Every day at 9:00 AM</td>
                      </tr>
                    </tbody>
                  </ExampleTable>
                </Popup.Content>
              </Popup>
            </CronInputWrapper>
            {cronErrors && <ErrorText>{cronErrors}</ErrorText>}
            {!cronErrors && surveySettings.default_reminder_schedule && (
              <SuccessText>✓ Valid cron expression</SuccessText>
            )}
          </Form.Field>


        </Form>
      </SectionCard>

      <ButtonContainer>
        <LeftButtons>
          <BackButton onClick={onBack}>Back</BackButton>
          <CancelButton onClick={onCancel}>Cancel Setup</CancelButton>
        </LeftButtons>
        <NextButton onClick={handleContinue} disabled={!!cronErrors || isSaving}>
          {isSaving ? 'Saving...' : 'Continue'}
        </NextButton>
      </ButtonContainer>
    </StepContainer>
  );
};

export default OrganizationWizardStep4Survey;

const StepContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
    padding-right: 32px;

`;

const StepHeader = styled.div`
  margin-bottom: 20px;
`;

const StepTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin: 0 0 4px 0;
`;

const StepSubtitle = styled.p`
  font-size: 13px;
  color: #666;
  margin: 0;
  line-height: 1.4;
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin: 16px 0 8px 0;
`;

const SectionCard = styled.div`
  padding: 16px;
  background: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #e0e0e0;
  margin-bottom: 12px;
`;

const HelpText = styled.div`
  font-size: 11px;
  color: #666;
  margin-top: 2px;
  font-style: italic;
`;

const ErrorText = styled.div`
  color: #db2828;
  font-size: 11px;
  margin-top: 2px;
  font-weight: 500;
`;

const SuccessText = styled.div`
  color: #21ba45;
  font-size: 11px;
  margin-top: 2px;
  font-weight: 500;
`;

const CronInputWrapper = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const IconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  background: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
  color: #666;
  padding: 0;

  &:hover {
    background: #f0f0f0;
    border-color: #bbb;
    color: #333;
  }

  i.icon {
    margin: 0 !important;
    font-size: 16px;
    line-height: 1;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const ExampleTable = styled.table`
  width: 100%;
  font-size: 12px;

  td {
    padding: 4px 0;
    vertical-align: top;
  }

  td:first-child {
    width: 35%;
    font-family: 'Courier New', monospace;
    color: #2185d0;
    padding-right: 12px;
  }

  code {
    background: #f8f9fa;
    padding: 2px 6px;
    border-radius: 3px;
    border: 1px solid #ddd;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #e0e0e0;
`;

const LeftButtons = styled.div`
  display: flex;
  gap: 12px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
`;

const BackButton = styled.button`
  padding: 10px 24px;
  background: transparent;
  border: 1px solid #d0d0d0;
  border-radius: 4px;
  color: #666;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: #f5f5f5;
    border-color: #b0b0b0;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CancelButton = styled.button`
  padding: 10px 24px;
  background: transparent;
  border: 1px solid #d0d0d0;
  border-radius: 4px;
  color: #666;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f5f5f5;
    border-color: #b0b0b0;
  }
`;

const NextButton = styled.button`
  padding: 10px 24px;
  background: #2185d0;
  border: none;
  border-radius: 4px;
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: #1678c2;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;
